import React from 'react';
import { Empty } from 'antd';

const scoreOf = (value) =>
  Number.isFinite(Number(value)) ? Number(value) : null;

const pointAt = (centerX, centerY, radius, index, count, value = 1) => {
  const angle = -Math.PI / 2 + (index * Math.PI * 2) / count;
  return [
    centerX + radius * value * Math.cos(angle),
    centerY + radius * value * Math.sin(angle),
  ];
};

const pointsText = (points) =>
  points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');

export const RadarChart = ({ categories = [], roles = [] }) => {
  if (categories.length < 3) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="表示できる軸がありません" />;
  }
  const width = 460;
  const height = 390;
  const centerX = width / 2;
  const centerY = 186;
  const radius = 118;
  const labelRadius = 150;
  const axisCount = categories.length;
  const visibleRoles = roles.filter((role) =>
    categories.some((category) => scoreOf(category.scores?.[role.key]) != null)
  );

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="admin360__radar"
      role="img"
      aria-label="カテゴリ別組織平均レーダーチャート"
    >
      {[1, 2, 3, 4, 5].map((level) => (
        <polygon
          key={`grid-${level}`}
          points={pointsText(
            categories.map((_, index) =>
              pointAt(
                centerX,
                centerY,
                radius,
                index,
                axisCount,
                level / 5
              )
            )
          )}
          fill="none"
          stroke={level === 5 ? '#D9D2BC' : '#EFE9D7'}
          strokeWidth="1"
        />
      ))}
      {categories.map((category, index) => {
        const [axisX, axisY] = pointAt(
          centerX,
          centerY,
          radius,
          index,
          axisCount
        );
        const [labelX, labelY] = pointAt(
          centerX,
          centerY,
          labelRadius,
          index,
          axisCount
        );
        const label =
          category.name.length > 12
            ? `${category.name.slice(0, 11)}…`
            : category.name;
        return (
          <g key={category.key || category.name}>
            <line
              x1={centerX}
              y1={centerY}
              x2={axisX}
              y2={axisY}
              stroke="#EFE9D7"
              strokeWidth="1"
            />
            <text
              x={labelX}
              y={labelY}
              textAnchor={Math.abs(labelX - centerX) < 10 ? 'middle' : labelX > centerX ? 'start' : 'end'}
              dominantBaseline="middle"
              fontSize={axisCount > 10 ? 9 : 11}
              fontWeight="600"
              fill="#23221F"
            >
              {label}
            </text>
          </g>
        );
      })}
      {visibleRoles.map((role) => {
        const points = categories.map((category, index) =>
          pointAt(
            centerX,
            centerY,
            radius,
            index,
            axisCount,
            (scoreOf(category.scores?.[role.key]) || 0) / 5
          )
        );
        return (
          <g key={role.key}>
            <polygon
              points={pointsText(points)}
              fill={role.key === 'self' ? role.color : 'none'}
              fillOpacity={role.key === 'self' ? 0.12 : 0}
              stroke={role.color}
              strokeWidth={role.key === 'self' ? 2.4 : 1.7}
              strokeDasharray={role.key === 'sub' ? '5 4' : undefined}
            />
            {points.map(([x, y], index) => (
              <circle
                key={`${role.key}-${categories[index].key || index}`}
                cx={x}
                cy={y}
                r="3"
                fill={role.key === 'sub' ? '#fff' : role.color}
                stroke={role.color}
                strokeWidth="1.4"
              />
            ))}
          </g>
        );
      })}
    </svg>
  );
};

export const TrendChart = ({ trend = [], expectedCount = 0 }) => {
  if (!trend.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="提出日時データがありません" />;
  }
  const width = 540;
  const height = 205;
  const padding = { left: 48, right: 20, top: 28, bottom: 34 };
  const firstTime = new Date(trend[0].date).getTime();
  const lastTime = new Date(trend[trend.length - 1].date).getTime();
  const timeSpan = Math.max(1, lastTime - firstTime);
  const total = Math.max(
    1,
    Number(expectedCount) || 0,
    trend[trend.length - 1].cumulative
  );
  const xOf = (date) =>
    padding.left +
    ((new Date(date).getTime() - firstTime) / timeSpan) *
      (width - padding.left - padding.right);
  const yOf = (value) =>
    height -
    padding.bottom -
    (value / total) * (height - padding.top - padding.bottom);
  const points = trend.map((item) => [
    xOf(item.date),
    yOf(item.cumulative),
    item,
  ]);
  const linePath = points
    .map(([x, y], index) => `${index ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`)
    .join(' ');
  const baseY = yOf(0);
  const areaPath = `${linePath} L${points[points.length - 1][0].toFixed(
    1
  )},${baseY.toFixed(1)} L${points[0][0].toFixed(1)},${baseY.toFixed(1)} Z`;
  const lastPoint = points[points.length - 1];
  const shortDate = (date) => date.slice(5).replace('-', '/');

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="admin360__trend-chart"
      role="img"
      aria-label="日別累積回答数"
    >
      {[0.25, 0.5, 0.75, 1].map((ratio) => {
        const y = yOf(total * ratio);
        return (
          <g key={ratio}>
            <line
              x1={padding.left}
              y1={y}
              x2={width - padding.right}
              y2={y}
              stroke="#EFE9D7"
            />
            <text
              x={padding.left - 7}
              y={y + 4}
              textAnchor="end"
              fontSize="11"
              fill="#A8A292"
            >
              {Math.round(ratio * 100)}%
            </text>
          </g>
        );
      })}
      <path d={areaPath} fill="#FF6B35" fillOpacity="0.1" />
      <path
        d={linePath}
        fill="none"
        stroke="#FF6B35"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      {points.map(([x, y, item]) => (
        <circle key={item.date} cx={x} cy={y} r="3" fill="#FF6B35" />
      ))}
      <circle
        cx={lastPoint[0]}
        cy={lastPoint[1]}
        r="5.5"
        fill="#fff"
        stroke="#FF6B35"
        strokeWidth="3"
      />
      <text
        x={Math.min(lastPoint[0], width - padding.right - 8)}
        y={Math.max(lastPoint[1] - 10, 13)}
        textAnchor="end"
        fontSize="12"
        fontWeight="700"
        fill="#D44A18"
      >
        {lastPoint[2].cumulative}件
      </text>
      <line
        x1={padding.left}
        y1={baseY}
        x2={width - padding.right}
        y2={baseY}
        stroke="#D9D2BC"
      />
      <text x={padding.left} y={height - 8} fontSize="11" fill="#6B6760">
        {shortDate(trend[0].date)}
      </text>
      <text
        x={width - padding.right}
        y={height - 8}
        textAnchor="end"
        fontSize="11"
        fill="#6B6760"
      >
        {shortDate(trend[trend.length - 1].date)}
      </text>
    </svg>
  );
};

export const ScoreHistogram = ({ distribution = [] }) => {
  const max = Math.max(1, ...distribution.map((bin) => bin.count));
  return (
    <div className="admin360__histogram" role="img" aria-label="他者評価総合スコア分布">
      {distribution.map((bin) => (
        <div className="admin360__histogram-column" key={bin.key}>
          <b>{bin.count}</b>
          <div className="admin360__histogram-track">
            <i style={{ height: `${(bin.count / max) * 100}%` }} />
          </div>
          <span>{bin.label}</span>
        </div>
      ))}
    </div>
  );
};
